import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Ticket, 
  Gift, 
  Settings, 
  LogOut,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

interface MenuItem {
  label: string;
  href: string;
  visible: boolean;
  highlight?: boolean;
  icon?: string;
}

interface MobileMenuProps {
  menuItems: MenuItem[];
  isAdmin: boolean;
  /** True if user is a Partner Admin (moderator) but NOT a full admin */
  isModerator?: boolean;
  isLoggedIn: boolean;
  onClose: () => void;
  onSignOut: () => void;
}

const iconMap: Record<string, React.ElementType> = {
  Calendar,
  Ticket,
  Gift,
  Trophy: Calendar, // fallback
};

export function MobileMenu({ 
  menuItems, 
  isAdmin, 
  isModerator = false,
  isLoggedIn, 
  onClose, 
  onSignOut 
}: MobileMenuProps) {
  const visibleItems = menuItems.filter(item => item.visible);

  const getIconForItem = (item: MenuItem) => {
    // Map menu items to appropriate icons
    if (item.label.toLowerCase().includes('match')) return Calendar;
    if (item.label.toLowerCase().includes('coupon')) return Ticket;
    if (item.label.toLowerCase().includes('credit') || item.label.toLowerCase().includes('reward')) return Gift;
    if (item.icon && iconMap[item.icon]) return iconMap[item.icon];
    return Calendar;
  };

  return (
    <div className="md:hidden border-t border-border/50 bg-card/98 backdrop-blur-xl animate-fade-in shadow-2xl shadow-black/20" role="navigation" aria-label="Main navigation menu">
      <nav className="flex flex-col pt-5 pb-6 px-4 max-h-[calc(100dvh-4rem)] overflow-y-auto safe-area-inset" aria-label="Mobile menu">
        {/* Primary Navigation */}
        <div className="space-y-1">
          {visibleItems.map((item, index) => {
            const IconComponent = getIconForItem(item);
            const isHighlight = item.highlight;
            
            return (
              <Link
                key={index}
                to={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3.5 py-3.5 px-4 rounded-xl min-h-[52px] transition-all duration-200",
                  "active:scale-[0.98] active:bg-muted/60",
                  isHighlight 
                    ? "text-amber-500 hover:bg-amber-500/10 group" 
                    : "text-foreground hover:bg-muted/50 group"
                )}
              >
                <div className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-xl transition-colors",
                  isHighlight 
                    ? "bg-amber-500/15 group-hover:bg-amber-500/20" 
                    : "bg-primary/10 group-hover:bg-primary/15"
                )}>
                  <IconComponent className={cn(
                    "h-5 w-5",
                    isHighlight ? "text-amber-500" : "text-primary"
                  )} />
                </div>
                <span className={cn(
                  "text-base font-semibold flex-1",
                  isHighlight ? "text-amber-500" : "text-foreground"
                )}>
                  {item.label}
                </span>
                <ChevronRight className={cn(
                  "h-4 w-4 opacity-40 transition-transform group-hover:translate-x-0.5",
                  isHighlight ? "text-amber-500" : "text-muted-foreground"
                )} />
              </Link>
            );
          })}
        </div>

        {/* Partner Admin Section - for moderators who are not full admins */}
        {isModerator && !isAdmin && (
          <>
            <div className="my-4 px-4">
              <Separator className="bg-border/50" />
            </div>
            <Link
              to="/admin"
              onClick={onClose}
              className="flex items-center gap-3.5 py-3.5 px-4 rounded-xl min-h-[52px] transition-all duration-200 text-blue-600 hover:bg-blue-500/10 active:scale-[0.98] group"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/15 transition-colors">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-[15px] font-medium flex-1">
                Partner Admin
              </span>
              <ChevronRight className="h-4 w-4 text-blue-600/50 opacity-40 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </>
        )}

        {/* Full Admin Section */}
        {isAdmin && (
          <>
            <div className="my-4 px-4">
              <Separator className="bg-border/50" />
            </div>
            <Link
              to="/admin"
              onClick={onClose}
              className="flex items-center gap-3.5 py-3.5 px-4 rounded-xl min-h-[52px] transition-all duration-200 text-emerald-600 hover:bg-emerald-500/10 active:scale-[0.98] group"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/15 transition-colors">
                <Settings className="h-5 w-5 text-emerald-600" />
              </div>
              <span className="text-[15px] font-medium flex-1">
                Admin Panel
              </span>
              <ChevronRight className="h-4 w-4 text-emerald-600/50 opacity-40 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </>
        )}

        {/* Sign Out Section */}
        {isLoggedIn && (
          <>
            <div className="my-4 px-4">
              <Separator className="bg-border/50" />
            </div>
            <button
              onClick={() => {
                onSignOut();
                onClose();
              }}
              className="flex items-center gap-3.5 py-3.5 px-4 rounded-xl min-h-[52px] transition-all duration-200 text-destructive hover:bg-destructive/10 active:scale-[0.98] group w-full text-left"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-destructive/10 group-hover:bg-destructive/15 transition-colors">
                <LogOut className="h-5 w-5 text-destructive" />
              </div>
              <span className="text-base font-semibold flex-1">
                Sign Out
              </span>
            </button>
          </>
        )}

        {/* Safe area padding for gesture bar */}
        <div className="h-safe-area-inset-bottom" />
      </nav>
    </div>
  );
}
